import React, { useMemo, useState } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { GoogleMap } from "@/lib/maps/primitives";

/**
 * CoachMap — single-marker map for the Vehicle Operations tab / Fleet
 * detail. Migrated to Google Maps (Phase — Map Provider Migration).
 * Provider-agnostic API preserved: {lat, lng, popupContent}. Motive
 * telemetry cadence + data model untouched. InfoWindow content matches
 * pre-migration exactly (whatever the caller supplied via popupContent).
 */
export const CoachMap = ({ lat, lng, popupContent, height, className = "" }) => {
  const [mapErr, setMapErr] = useState(null);

  const hasCoords = lat != null && lng != null;

  // Google Maps InfoWindows accept HTML strings. Convert the React
  // popupContent to a static markup string so the caller's JSX still
  // renders inside the info bubble.
  const markers = useMemo(() => {
    if (!hasCoords) return [];
    const info = popupContent
      ? `<div style="font-size:12px;line-height:1.4">${renderToStaticMarkup(popupContent)}</div>`
      : null;
    return [{ id: "coach", lat, lng, info }];
  }, [lat, lng, popupContent, hasCoords]);

  if (!hasCoords || mapErr) {
    return (
      <div
        className={`rounded-md flex items-center justify-center text-[13.5px] ${className}`}
        style={{
          height: height || "320px",
          background: "var(--bg-inset)",
          border: "1px solid var(--border-default)",
          color: "var(--fg-secondary)",
        }}
        data-testid="coach-map-empty"
      >
        {mapErr
          ? "Map unavailable"
          : "Location unavailable — Waiting for Motive vehicle telemetry"}
      </div>
    );
  }

  return (
    <div
      className={`rounded-md overflow-hidden ${className}`}
      style={{
        height: height || "320px",
        border: "1px solid var(--border-default)",
      }}
      data-testid="coach-map"
    >
      <GoogleMap
        markers={markers}
        zoom={11}
        fit="auto"
        onError={setMapErr}
      />
    </div>
  );
};
